import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { History, RotateCcw, Loader2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PAGE = 25;
const RESTORABLE = ['update', 'delete', 'register', 'diploma_update', 'deliver', 'undeliver', 'bulk_delete', 'bulk_update', 'start_level_change'];
const LVL = { nybegynner: 'Nybegynner', viderekommende: 'Viderekommende', bronse: 'Bronse', solv: 'Sølv', gull: 'Gull' };
const ALL_ACTIONS = 'register,update,delete,diploma_update,deliver,undeliver,restore,bulk_delete,bulk_update,start_level_change';

const TAGS = {
  register: { text: 'Registrert', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  update: { text: 'Endret', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  delete: { text: 'Slettet', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  diploma_update: { text: 'Diplom endret', cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  deliver: { text: 'Utdelt', cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  undeliver: { text: 'Utdeling angret', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  restore: { text: 'Gjenopprettet', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  bulk_delete: { text: 'Massesletting', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  bulk_update: { text: 'Masseendring', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  start_level_change: { text: 'Startnivå endret', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
};

const ROLE = { admin: 'Admin', super_admin: 'SuperAdmin', org_admin: 'Skyteleder', range_officer: 'Skyteleder' };

const FILTERS = [
  { value: '', label: 'Alle typer' },
  { value: 'register', label: 'Registrert' },
  { value: 'update,bulk_update', label: 'Endret' },
  { value: 'delete,bulk_delete', label: 'Slettet' },
  { value: 'start_level_change', label: 'Startnivå endret' },
  { value: 'diploma_update', label: 'Diplom endret' },
  { value: 'deliver,undeliver', label: 'Utdeling' },
  { value: 'restore', label: 'Gjenopprettet' },
];

const fmtD = (d) => {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (d || '–');
};
const fmtTs = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })} kl. ${d.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}`;
};

const describe = (log, vName) => {
  const d = log.details || {};
  const v = vName(d.discipline_key, d.variant_key);
  switch (log.action) {
    case 'register': {
      const names = (d.members || []).map(m => m.name).filter(Boolean);
      return `${d.series_count} serier ${fmtD(d.result_date)}${names.length ? ` – ${names.join(', ')}` : ''}${d.awards_created?.length ? ` · ${d.awards_created.length} nye merker` : ''}`;
    }
    case 'update': {
      const c = d.changes || {};
      const b = d.before || {};
      const parts = [];
      if ('score' in c) parts.push(`poeng ${d.old_score ?? b.score} → ${c.score}`);
      if ('result_date' in c) parts.push(`dato ${b.result_date ? fmtD(b.result_date) : '?'} → ${fmtD(c.result_date)}`);
      if ('program' in c) parts.push(`program «${b.program ?? ''}» → «${c.program}»`);
      if ('notes' in c) parts.push('notat endret');
      return `${d.member_name} – ${v}: ${parts.join(', ') || 'ingen endring'}`;
    }
    case 'delete':
      return `${d.member_name} – ${v}: serie med ${d.score} poeng (${fmtD(d.result_date)}) slettet`;
    case 'diploma_update': {
      const c = d.changes || {};
      const parts = [];
      if (c.achieved_at) parts.push(`dato ${fmtD(c.achieved_at.old)} → ${fmtD(c.achieved_at.new)}`);
      if (c.logo_mode) parts.push(c.logo_mode.new === 'logo' ? 'klubbnavn → logo' : 'logo → klubbnavn');
      if (c.show_footer) parts.push(`Aktivlogg-linje ${c.show_footer.new ? 'på' : 'av'}`);
      return `${d.member_name} – ${d.level_name}-diplom: ${parts.join(', ')}`;
    }
    case 'deliver':
      return `${d.member_name}: ${d.level_name}-merke markert som utdelt`;
    case 'undeliver':
      return `${d.member_name}: ${d.level_name}-merke satt tilbake til «ikke utdelt»`;
    case 'restore':
      return d.summary || 'Hendelse angret';
    case 'bulk_delete':
      return `${d.count} serier slettet – ${(d.members || []).map(m => m.name).filter(Boolean).join(', ')}`;
    case 'bulk_update': {
      const c = d.changes || {};
      const parts = [];
      if (c.result_date) parts.push(`dato → ${fmtD(c.result_date)}`);
      if ('program' in c) parts.push(`program → «${c.program}»`);
      return `${d.count} serier: ${parts.join(', ')} – ${(d.members || []).map(m => m.name).filter(Boolean).join(', ')}`;
    }
    case 'start_level_change':
      return (d.changes || []).map(c => `${c.member_name} – ${vName(c.discipline_key, c.variant_key)}: ${LVL[c.old] || c.old} → ${LVL[c.new] || c.new}`).join(' · ');
    default:
      return '';
  }
};

const LogRow = ({ log, vName, onRestore, busy }) => {
  const tag = TAGS[log.action] || { text: log.action, cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' };
  const [confirm, setConfirm] = useState(false);
  const canRestore = RESTORABLE.includes(log.action) && !log.restored_at;
  return (
    <div className="p-3 rounded-lg bg-gray-700/40 border border-gray-700" data-testid={`changelog-row-${log.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${tag.cls}`}>{tag.text}</span>
        <span className="text-xs text-gray-400" data-testid={`changelog-ts-${log.id}`}>{fmtTs(log.created_at)}</span>
        <span className="text-xs text-gray-400">av <strong className="text-gray-200">{log.user_name}</strong>{ROLE[log.user_role] ? ` (${ROLE[log.user_role]})` : ''}</span>
      </div>
      <p className="text-sm text-gray-200 mt-1 break-words" data-testid={`changelog-desc-${log.id}`}>{describe(log, vName)}</p>
      {log.restored_at && (
        <p className="text-xs text-purple-300 mt-1 flex items-center gap-1" data-testid={`changelog-restored-${log.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />Angret av {log.restored_by_name} – {fmtTs(log.restored_at)}
        </p>
      )}
      {canRestore && (
        <div className="mt-2 flex flex-wrap gap-2">
          {!confirm ? (
            <button type="button" onClick={() => setConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border border-gray-600 text-gray-200 text-sm hover:bg-gray-600/40 touch-manipulation"
              data-testid={`changelog-restore-btn-${log.id}`}>
              <RotateCcw className="w-4 h-4" />Angre
            </button>
          ) : (
            <>
              <span className="text-xs text-gray-300 self-center basis-full">Angre denne endringen? Merkeprogresjonen for medlemmet regnes ut på nytt.</span>
              <button type="button" disabled={busy} onClick={() => onRestore(log)}
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg bg-yellow-500 text-gray-900 text-sm font-semibold disabled:opacity-50 touch-manipulation"
                data-testid={`changelog-restore-confirm-${log.id}`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}Ja, angre
              </button>
              <button type="button" onClick={() => setConfirm(false)} className="px-3 h-10 rounded-lg text-gray-300 text-sm hover:bg-gray-600/40"
                data-testid={`changelog-restore-cancel-${log.id}`}>Avbryt</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const MerkeChangeLog = ({ config, members, onRestored, refreshSignal }) => {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [fAction, setFAction] = useState('');
  const [fMember, setFMember] = useState('');
  const [open, setOpen] = useState(false);

  const vName = useMemo(() => (dk, vk) => {
    const d = config?.disciplines?.find(x => x.key === dk);
    const v = d?.variants?.find(x => x.key === vk);
    return d ? `${d.name}${v ? ` / ${v.name}` : ''}` : (vk || '');
  }, [config]);

  const fetchPage = useCallback(async (skip) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ limit: PAGE, skip });
    params.set('action', fAction || ALL_ACTIONS);
    if (fMember) params.set('member_id', fMember);
    const r = await axios.get(`${API_BASE}/api/merkeskyting/audit-logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    return r.data;
  }, [fAction, fMember]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchPage(0);
      setLogs(d.logs || []);
      setHasMore(!!d.has_more);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [fetchPage]);

  useEffect(() => { if (open) reload(); }, [reload, refreshSignal, open]);

  const loadMore = async () => {
    try {
      const d = await fetchPage(logs.length);
      setLogs(prev => [...prev, ...(d.logs || []).filter(n => !prev.some(p => p.id === n.id))]);
      setHasMore(!!d.has_more);
    } catch {
      toast.error('Kunne ikke hente flere hendelser');
    }
  };

  const restore = async (log) => {
    setBusyId(log.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/api/merkeskyting/audit-logs/${log.id}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.summary || 'Endringen er angret');
      await reload();
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke angre');
    } finally { setBusyId(null); }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-3" data-testid="merke-changelog">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="w-full flex items-center gap-2 text-left touch-manipulation" data-testid="changelog-toggle">
        <History className="w-5 h-5 text-yellow-400" />
        <h3 className="text-white font-semibold flex-1">Endringslogg</h3>
        {open ? <ChevronUp className="w-5 h-5 text-gray-300" /> : <ChevronDown className="w-5 h-5 text-gray-300" />}
      </button>
      {open && (<>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select value={fAction} onChange={(e) => setFAction(e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm" data-testid="changelog-filter-action">
          {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select value={fMember} onChange={(e) => setFMember(e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm" data-testid="changelog-filter-member">
          <option value="">Alle medlemmer</option>
          {(members || []).map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="text-gray-400 text-sm py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Laster endringslogg…</div>
      ) : logs.length === 0 ? (
        <p className="text-gray-400 text-sm py-4 text-center" data-testid="changelog-empty">Ingen endringer funnet.</p>
      ) : (
        <div className="space-y-2">
          {logs.map(l => <LogRow key={l.id} log={l} vName={vName} onRestore={restore} busy={busyId === l.id} />)}
        </div>
      )}
      {hasMore && !loading && (
        <button type="button" onClick={loadMore} className="w-full h-10 rounded-lg border border-gray-600 text-gray-200 text-sm hover:bg-gray-700"
          data-testid="changelog-load-more">Vis flere</button>
      )}
      </>)}
    </div>
  );
};

export default MerkeChangeLog;
